// The docking layout, Unity style: the page split into rows and columns of tab groups. Drag a tab to
// another group's tabs, or to one of its sides for a new group there, or to the very top or bottom of
// one to stack it with the panel in front there (one tab showing both, one under another, each under
// its title, which drags it out again); drag the gaps between groups to resize them; double-click a tab
// to fill the page with its group; right-click a tab to rename it (inline). A group of one tab shows no strip, only a thin grip along its top
// (its tab when the pointer is on it). The layout is kept in this browser (per page, wide and narrow
// screens apart). The model is tree.ts.
//
// Panels are elements moved into the groups, never rebuilt, so their ids and wiring stay as they are; a
// closed one, or one not shown on this page, waits in the parking element (still in the document). A panel
// can be lent out (lend: to a popup or a FloatWin); its tab then holds a note with a button to bring it back.

import { type Group, type Node as Tree, type Path, type Zone, VIEWPORT, activate, at, closeTab, entries, findTab, group, groups, largest, moveTab, moveTabs, resize, split, stackOf, tabs, validate } from './tree.ts';

/** listable false: never stacked (the View, which fills its group). */
export interface Panel { id: string; title: string; el: HTMLElement; closable?: boolean; listable?: boolean }

const MIN_PX = 80;   // the least a group shrinks to while its neighbour is dragged
const EDGE = 0.25;   // the share of a group, along each side, that drops a tab beside it rather than in it
const BAND = 28;     // px along a group's very top and bottom (at most 12 % of it) that stack a tab with the one in front
// flex-grow per unit of size: grows summing below 1 (the shown children of a split some of whose children
// are hidden) would leave part of the room empty
const GROW = 1000;

const div = (cls: string, text?: string) => {
  const d = document.createElement('div');
  d.className = cls;
  if (text) d.textContent = text;
  return d;
};

export class Dock {
  private panels = new Map<string, Panel>();
  private tree: Tree;
  private closed = new Set<string>();
  /** A closed tab: one it shared a group with, where it opens again. */
  private last: Record<string, string> = {};
  /** Tabs renamed (right-click), by nameKey: a panel's id, a stack's ids. */
  private names: Record<string, string> = {};
  /** A tab of the group filling the page (double-click), or null. */
  private max: string | null = null;
  private narrow: boolean;
  private queued = false;
  /** Panels lent out (lend), each with what brings it back. */
  private lent = new Map<string, () => void>();
  /** After the layout changes shape (not on a tab switch or a resize). */
  onChange?: () => void;
  /** After a tab comes to the front (a click, or the layout drawn again): see showing(). */
  onFront?: () => void;

  constructor(private root: HTMLElement, private park: HTMLElement, private page: string, private wide: Tree) {
    const mq = matchMedia('(max-width: 600px)');
    this.narrow = mq.matches;
    this.tree = this.load();
    mq.addEventListener('change', () => { this.narrow = mq.matches; this.max = null; this.tree = this.load(); this.placeAll(); this.render(); });
    root.addEventListener('pointerdown', e => this.down(e));
    root.addEventListener('click', e => this.click(e));
    root.addEventListener('auxclick', e => { if (e.button === 1) { const t = this.tabAt(e.target); if (t) this.close(t); } });
    root.addEventListener('contextmenu', e => {
      const t = (e.target as HTMLElement).closest<HTMLElement>('.dock-tab');
      if (!t || (e.target as HTMLElement).closest('input')) return;
      e.preventDefault();
      this.rename(t);
    });
    root.addEventListener('dblclick', e => {
      const t = this.tabAt(e.target);
      if (!t || (e.target as HTMLElement).closest('.dock-x, input')) return;
      this.max = this.max ? null : t;
      this.render();
    });
  }

  /** Every element of the parking spot with data-tab: a panel each, titled by data-title; data-editor ones unless editorOnly is false; data-nolist ones never stacked. */
  registerFrom(holder: HTMLElement, editorOnly: boolean) {
    for (const el of holder.querySelectorAll<HTMLElement>(':scope > [data-tab]')) {
      if (el.hasAttribute('data-editor') !== editorOnly) continue;
      const id = el.dataset.tab!;
      this.register({ id, title: el.dataset.title || id, el, closable: id !== VIEWPORT, listable: id !== VIEWPORT && !el.hasAttribute('data-nolist') });
    }
  }

  register(p: Panel) {
    this.panels.set(p.id, p);
    if (!findTab(this.tree, p.id) && !this.closed.has(p.id)) this.place(p.id, false);
    this.queue();
  }

  has = (id: string) => this.panels.has(id);
  isOpen = (id: string) => !!findTab(this.tree, id);
  /** Whether the tab is on the page and in front of its group. */
  showing = (id: string) => { const el = this.panels.get(id)?.el; return !!el && !this.lent.has(id) && el.classList.contains('dock-on') && this.root.contains(el); };

  /** Open the tab (where it last was, else where it starts) and bring it to the front. */
  show(id: string) {
    if (!this.panels.has(id)) return;
    this.closed.delete(id);
    if (findTab(this.tree, id)) this.tree = activate(this.tree, id);
    else this.place(id, true);
    if (this.max && findTab(this.tree, this.max)?.join() !== findTab(this.tree, id)?.join()) this.max = null;
    this.save();
    this.render();
  }

  close(id: string) {
    if (this.panels.get(id)?.closable === false) return;
    const path = findTab(this.tree, id);
    if (!path) return;
    const mates = (at(this.tree, path) as Group).tabs.filter(t => t !== id);
    const mate = mates.find(t => this.panels.has(t)) ?? mates[0];
    if (mate) this.last[id] = mate; else delete this.last[id];
    this.tree = closeTab(this.tree, id);
    this.closed.add(id);
    if (this.max === id) this.max = null;
    this.save();
    this.render();
  }

  /** Take a panel's element out of the layout, for a popup or a window to hold; its tab shows a note until unlend. back: the note's button (it should end in unlend). */
  lend(id: string, back: () => void): HTMLElement | null {
    const p = this.panels.get(id);
    if (!p) return null;
    this.lent.set(id, back);
    this.render();
    return p.el;
  }

  /** The lent panel back in its tab. */
  unlend(id: string) {
    if (!this.lent.delete(id)) return;
    this.render();
  }

  isLent = (id: string) => this.lent.has(id);

  /** Back to the page's starting layout. */
  reset() {
    this.tree = this.defaults();
    this.closed.clear();
    this.last = {};
    this.names = {};
    this.max = null;
    this.placeAll();
    this.save();
    this.render();
  }

  /** A button opening the list of panels (ticked when open) and Reset layout. */
  menu(btn: HTMLElement) {
    let m: HTMLElement | null = null;
    const shut = () => { m?.remove(); m = null; btn.classList.remove('on'); };
    btn.onclick = () => {
      if (m) { shut(); return; }
      m = div('omenu dock-menu');
      const order = tabs(this.defaults());
      const ids = [...this.panels.keys()].sort((a, b) => (order.indexOf(a) + 1 || 999) - (order.indexOf(b) + 1 || 999));
      for (const id of ids) {
        const p = this.panels.get(id)!, open = this.isOpen(id), b = document.createElement('button');
        b.textContent = `${open ? '✓' : ' '} ${this.nameOf([id])}`;
        b.disabled = p.closable === false;
        b.onclick = () => { shut(); if (open) this.close(id); else this.show(id); };
        m.append(b);
      }
      const r = document.createElement('button');
      r.textContent = '  Reset layout';
      r.className = 'dock-reset';
      r.onclick = () => { shut(); this.reset(); };
      m.append(r);
      document.body.append(m);
      const br = btn.getBoundingClientRect();
      m.style.top = `${br.bottom + 4}px`;
      m.style.left = `${Math.max(8, Math.min(br.right - m.offsetWidth, innerWidth - m.offsetWidth - 8))}px`;
      btn.classList.add('on');
    };
    addEventListener('pointerdown', e => { if (m && !m.contains(e.target as Node) && !btn.contains(e.target as Node)) shut(); }, true);
    addEventListener('keydown', e => { if (m && e.key === 'Escape') { shut(); e.stopPropagation(); } }, true);
  }

  // ── the saved layout ──────────────────────────────────────────────────

  private get key() { return `cloudview.layout.v1:${this.page}:${this.narrow ? 'narrow' : 'wide'}`; }

  /** Narrow screens: the view on top, every other tab in one group under it. */
  private defaults(): Tree {
    if (!this.narrow) return this.wide;
    return split('col', [group([VIEWPORT]), group(tabs(this.wide).filter(t => t !== VIEWPORT))], [0.6, 0.4]);
  }

  private load(): Tree {
    this.closed.clear();
    this.last = {};
    this.names = {};
    try {
      const s = JSON.parse(localStorage.getItem(this.key) || 'null');
      const t = validate(s?.tree);
      if (t) {
        if (Array.isArray(s.closed)) for (const c of s.closed) if (typeof c === 'string' && !findTab(t, c)) this.closed.add(c);
        if (s.last && typeof s.last === 'object') for (const [k, v] of Object.entries(s.last)) if (typeof v === 'string') this.last[k] = v;
        if (s.names && typeof s.names === 'object') for (const [k, v] of Object.entries(s.names)) if (typeof v === 'string' && v) this.names[k] = v;
        return t;
      }
    } catch { /* storage blocked, or not JSON: the defaults */ }
    return this.defaults();
  }

  private save() {
    try { localStorage.setItem(this.key, JSON.stringify({ tree: this.tree, closed: [...this.closed], last: this.last, names: this.names })); } catch { /* storage blocked */ }
  }

  /** Where a tab not in the layout goes: beside the tab it last shared a group with, else one it starts with, else the biggest group but the view's. */
  private home(id: string): Path {
    const near = [this.last[id], ...(groups(this.defaults()).find(x => x.g.tabs.includes(id))?.g.tabs ?? [])];
    for (const t of near) {
      const p = t && t !== id ? findTab(this.tree, t) : null;
      if (p) return p;
    }
    const shown = (g: Group) => g.tabs.some(t => this.panels.has(t));
    return largest(this.tree, g => !shown(g) || g.tabs.includes(VIEWPORT)) ?? largest(this.tree) ?? [];
  }

  private place(id: string, front: boolean) {
    const path = this.home(id), g = at(this.tree, path);
    const before = g?.kind === 'group' ? g.active : null;
    this.tree = moveTab(this.tree, id, path, 'center');
    if (!front && before && this.panels.has(before)) this.tree = activate(this.tree, before);
  }

  private placeAll() {
    for (const id of this.panels.keys()) if (!findTab(this.tree, id) && !this.closed.has(id)) this.place(id, false);
  }

  // ── drawing ───────────────────────────────────────────────────────────

  private queue() {
    if (this.queued) return;
    this.queued = true;
    queueMicrotask(() => { this.queued = false; this.render(); });
  }

  private shown = (n: Tree): boolean => n.kind === 'group' ? n.tabs.some(t => this.panels.has(t)) : n.children.some(this.shown);

  render() {
    const scroll = [...this.panels.values()].map(p => [p.el, p.el.scrollTop] as const);
    const stacks = new Map([...this.root.querySelectorAll<HTMLElement>('.dock-stack')].map(l => [l.dataset.key, l.scrollTop]));
    this.root.replaceChildren();
    const mp = this.max ? findTab(this.tree, this.max) : null;
    const top = mp ? this.groupEl(at(this.tree, mp) as Group, mp) : this.build(this.tree, []);
    if (top) this.root.append(top);
    for (const p of this.panels.values()) if (!this.lent.has(p.id) && !this.root.contains(p.el)) this.park.append(p.el);
    for (const [el, y] of scroll) if (y) el.scrollTop = y;
    for (const l of this.root.querySelectorAll<HTMLElement>('.dock-stack')) l.scrollTop = stacks.get(l.dataset.key) ?? 0;
    this.onChange?.();
    this.onFront?.();
  }

  private build(n: Tree, path: Path): HTMLElement | null {
    if (!this.shown(n)) return null;
    if (n.kind === 'group') return this.groupEl(n, path);
    const kids = n.children.map((c, i) => [i, this.build(c, [...path, i])] as const).filter((k): k is readonly [number, HTMLElement] => !!k[1]);
    if (kids.length === 1) return kids[0][1];
    const el = div(`dock-split dock-${n.dir}`);
    kids.forEach(([i, k], j) => {
      if (j) el.append(this.splitter(n, path, kids[j - 1][0], i, kids[j - 1][1], k));
      k.style.flex = `${n.sizes[i] * GROW} 1 0px`;
      el.append(k);
    });
    return el;
  }

  /** A group: its tabs over the one in front (a stack's panels one under another, each under its title); one tab: no strip but a grip. */
  private groupEl(g: Group, path: Path): HTMLElement {
    const ids = g.tabs.filter(t => this.panels.has(t)), ents = entries({ ...g, tabs: ids });
    const on = ids.includes(g.active) ? g.active : ids[0], onE = ents.find(e => e.includes(on));
    const el = div(`dock-group${ents.length < 2 ? ' dock-solo' : ''}`), strip = div('dock-tabs'), body = div('dock-body');
    el.dataset.path = path.join('.');
    el.dataset.active = on;   // for the CSS: the View's group is see-through
    for (const e of ents) {
      strip.append(this.tabEl(e, e === onE));
      if (e.length === 1) { this.panelEl(e[0], e === onE, body); continue; }
      const st = div(`dock-stack${e === onE ? ' dock-on' : ''}`);
      st.dataset.key = e.join(' ');
      for (const id of e) { st.append(this.tabEl([id], false, ' dock-head')); this.panelEl(id, e === onE, st); }
      body.append(st);
    }
    if (this.max) strip.append(div('dock-maxed', '⤡'));
    el.append(strip, body);
    return el;
  }

  /** The tab of a panel, or of a stack (its titles; data-tab its first, data-stack all of them); cls ' dock-head': a stacked panel's title. */
  private tabEl(ids: string[], on: boolean, cls = ''): HTMLElement {
    const ps = ids.map(id => this.panels.get(id)!), name = this.nameOf(ids), t = div(`dock-tab${on ? ' on' : ''}${cls}`, name);
    t.title = `${name}${name !== this.nameOf(ids, true) ? ` (${this.nameOf(ids, true)})` : ''}: right-click to rename`;
    t.dataset.tab = ids[0];
    if (ids.length > 1) t.dataset.stack = ids.join(' ');
    if (ps.every(p => p.closable !== false)) { const x = document.createElement('span'); x.className = 'dock-x'; x.textContent = '✕'; x.title = 'close'; t.append(x); }
    return t;
  }

  /** A panel into its place (or, lent, a note with the button that brings it back). */
  private panelEl(id: string, on: boolean, into: HTMLElement) {
    const p = this.panels.get(id)!, back = this.lent.get(id);
    if (back) {
      const note = div(`pane dock-lent${on ? ' dock-on' : ''}`, `${p.title} is open in a window. `);
      note.dataset.lent = id;
      const b = document.createElement('button');
      b.className = 'small';
      b.textContent = 'Bring back';
      b.onclick = back;
      note.append(b);
      into.append(note);
      return;
    }
    p.el.classList.toggle('dock-on', on);
    into.append(p.el);
  }

  /** The gap between children i and j of the split at path (elements a and b): drag to share their room. */
  private splitter(n: Tree & { kind: 'split' }, path: Path, i: number, j: number, a: HTMLElement, b: HTMLElement): HTMLElement {
    const s = div('dock-splitter');
    s.onpointerdown = e => {
      if (e.button !== 0) return;
      e.preventDefault();
      s.setPointerCapture(e.pointerId);
      const row = n.dir === 'row', box = s.parentElement!.getBoundingClientRect();
      const px = row ? box.width : box.height, start = row ? e.clientX : e.clientY, from = this.tree;
      // flex shares the room among the shown children by size: px per unit of size
      const shownSum = [...s.parentElement!.children].filter(c => !c.classList.contains('dock-splitter'))
        .reduce((sum, c) => sum + (parseFloat((c as HTMLElement).style.flexGrow) || 0), 0) / GROW || 1;
      const unit = shownSum / Math.max(1, px);
      s.classList.add('on');
      s.onpointermove = m => {
        this.tree = resize(from, path, i, j, ((row ? m.clientX : m.clientY) - start) * unit, MIN_PX * unit);
        const now = at(this.tree, path) as typeof n;
        a.style.flexGrow = String(now.sizes[i] * GROW);
        b.style.flexGrow = String(now.sizes[j] * GROW);
      };
      s.onpointerup = s.onpointercancel = () => {
        s.onpointermove = s.onpointerup = s.onpointercancel = null;
        s.classList.remove('on');
        if (this.tree !== from) this.save();
      };
    };
    return s;
  }

  // ── tabs: click, close, drag ──────────────────────────────────────────

  private tabAt(t: EventTarget | null): string | null {
    return (t as HTMLElement | null)?.closest?.<HTMLElement>('.dock-tab')?.dataset.tab ?? null;
  }

  /** The panels of the tab under t: a stack's tab, all of them. */
  private idsAt(t: EventTarget | null): string[] {
    const e = (t as HTMLElement | null)?.closest?.<HTMLElement>('.dock-tab');
    return e?.dataset.stack?.split(' ') ?? (e?.dataset.tab ? [e.dataset.tab] : []);
  }

  private dragged = false;

  /** A panel's or stack's name: renamed, else its title(s); dflt: never the renamed one. */
  private nameKey = (ids: string[]) => [...ids].sort().join(' ');
  private nameOf(ids: string[], dflt = false): string {
    return (!dflt && this.names[this.nameKey(ids)]) || ids.map(id => (!dflt && this.names[id]) || this.panels.get(id)?.title || id).join(' + ');
  }

  /** Rename a tab in place: Enter (or clicking away) keeps it, Esc doesn't; empty: back to its own name. */
  private rename(t: HTMLElement) {
    const ids = this.idsAt(t), key = this.nameKey(ids), dflt = this.nameOf(ids, true), label = t.firstChild;
    if (!ids.length || !label) return;
    const inp = document.createElement('input');
    inp.className = 'dock-rename';
    inp.value = this.nameOf(ids);
    inp.size = Math.max(6, inp.value.length);
    let done = false;
    const end = (ok: boolean) => {
      if (done) return;
      done = true;
      const v = inp.value.trim().slice(0, 60);
      if (ok && v !== this.nameOf(ids)) {
        if (!v || v === dflt) delete this.names[key]; else this.names[key] = v;
        this.save();
      }
      this.render();
    };
    inp.onkeydown = k => {
      k.stopPropagation();   // not the page's keys (the editor's Delete, …)
      if (k.key === 'Enter') end(true); else if (k.key === 'Escape') end(false);
    };
    inp.oninput = () => { inp.size = Math.max(6, inp.value.length); };
    inp.onblur = () => end(true);
    label.replaceWith(inp);
    inp.focus();
    inp.select();
  }

  private click(e: MouseEvent) {
    const id = this.tabAt(e.target);
    if (!id || this.dragged) { this.dragged = false; return; }
    if ((e.target as HTMLElement).closest('input')) return;
    if ((e.target as HTMLElement).closest('.dock-x')) { for (const t of this.idsAt(e.target)) this.close(t); return; }
    this.front(id);
  }

  /** The tab (and its stack) to the front of its group, without redrawing the layout. */
  private front(id: string) {
    this.tree = activate(this.tree, id);
    const g = this.root.querySelector<HTMLElement>(`.dock-tab[data-tab="${CSS.escape(id)}"]`)?.closest<HTMLElement>('.dock-group');
    if (g) {
      g.dataset.active = id;
      const path = g.dataset.path ? g.dataset.path.split('.').map(Number) : [], grp = at(this.tree, path);
      const on = new Set(grp?.kind === 'group' ? stackOf(grp, id) : [id]);
      for (const t of g.querySelectorAll<HTMLElement>(':scope > .dock-tabs > .dock-tab')) t.classList.toggle('on', on.has(t.dataset.tab!));
      for (const t of g.querySelectorAll<HTMLElement>(':scope > .dock-tabs > .dock-tab, :scope > .dock-body > .dock-stack > .dock-tab')) this.panels.get(t.dataset.tab!)?.el.classList.toggle('dock-on', on.has(t.dataset.tab!));
      for (const s of g.querySelectorAll<HTMLElement>(':scope > .dock-body > .dock-stack')) s.classList.toggle('dock-on', on.has(s.dataset.key!.split(' ')[0]));
      for (const n of g.querySelectorAll<HTMLElement>('.dock-lent')) n.classList.toggle('dock-on', on.has(n.dataset.lent!));
    }
    this.save();
    this.onChange?.();
    this.onFront?.();
  }

  private down(e: PointerEvent) {
    const ids = this.idsAt(e.target);
    if (!ids.length || e.button !== 0 || (e.target as HTMLElement).closest('.dock-x, input')) return;
    const x0 = e.clientX, y0 = e.clientY;
    let ghost: HTMLElement | null = null, drop: HTMLElement | null = null;
    let target: { path: Path; zone: Zone; before?: string } | null = null;
    this.dragged = false;
    const move = (m: PointerEvent) => {
      if (!ghost) {
        if (Math.hypot(m.clientX - x0, m.clientY - y0) < 5) return;
        this.dragged = true;
        ghost = div('dock-ghost', this.nameOf(ids));
        drop = div('dock-drop');
        drop.hidden = true;
        document.body.append(ghost, drop);
      }
      ghost.style.transform = `translate(${m.clientX + 12}px, ${m.clientY + 8}px)`;
      target = this.aim(ids, m.clientX, m.clientY, drop!);
    };
    const end = (ok: boolean) => {
      removeEventListener('pointermove', move, true);
      removeEventListener('pointerup', up, true);
      removeEventListener('pointercancel', cancel, true);
      removeEventListener('keydown', key, true);
      ghost?.remove(); drop?.remove();
      // the click a drag ends in (pointer down and up on the same tab) is not a click; with none, the next one is
      setTimeout(() => { this.dragged = false; });
      if (!ok || !target) return;
      const t = moveTabs(this.tree, ids, target.path, target.zone, target.before);
      if (t === this.tree) return;
      this.tree = t;
      this.max = null;
      this.save();
      this.render();
    };
    const up = () => end(this.dragged);
    const cancel = () => end(false);
    const key = (k: KeyboardEvent) => { if (k.key === 'Escape') { k.stopPropagation(); end(false); } };
    addEventListener('pointermove', move, true);
    addEventListener('pointerup', up, true);
    addEventListener('pointercancel', cancel, true);
    addEventListener('keydown', key, true);
  }

  /**
   * The drop of tabs (a stack's: all of them) under the pointer, shown on the drop element: in a group's
   * tabs (before one), a zone of its body, its very top or bottom (stacked with the tab in front, first or
   * last), or, over a stack, between two of its panels.
   */
  private aim(ids: string[], x: number, y: number, drop: HTMLElement): { path: Path; zone: Zone; before?: string } | null {
    // by their boxes: the View's group lets the pointer through, so hit testing never finds it
    const g = [...this.root.querySelectorAll<HTMLElement>('.dock-group')].find(e => {
      const r = e.getBoundingClientRect();
      return x >= r.left && x < r.right && y >= r.top && y < r.bottom;
    });
    if (!g) { drop.hidden = true; return null; }
    const path = g.dataset.path ? g.dataset.path.split('.').map(Number) : [];
    drop.hidden = false;
    const put = (l: number, t: number, w: number, h: number, caret = false) => {
      drop.classList.toggle('caret', caret);
      Object.assign(drop.style, { left: `${l}px`, top: `${t}px`, width: `${w}px`, height: `${h}px` });
    };
    // the strip (a lone tab's grip: while it is open)
    const strip = g.querySelector<HTMLElement>(':scope > .dock-tabs')!, sr = strip.getBoundingClientRect();
    if (y <= sr.bottom && (!g.classList.contains('dock-solo') || sr.height > 8)) {
      const tabs = [...strip.querySelectorAll<HTMLElement>('.dock-tab')];
      // the strip wraps: the first tab on a row below the pointer, or left of it on its row
      const next = tabs.find(t => { const r = t.getBoundingClientRect(); return y < r.top || (y <= r.bottom && x < r.left + r.width / 2); });
      const r = (next ?? tabs[tabs.length - 1])?.getBoundingClientRect();
      put(r ? (next ? r.left : r.right) - 1 : sr.left, r ? r.top : sr.top, 3, r ? r.height : sr.height, true);
      return { path, zone: 'center', before: next?.dataset.tab };
    }
    const bodyEl = g.querySelector<HTMLElement>(':scope > .dock-body')!, b = bodyEl.getBoundingClientRect();
    const grp = at(this.tree, path), listable = (t: string) => this.panels.get(t)?.listable !== false;
    const front = grp?.kind === 'group' ? stackOf(grp, g.dataset.active!).filter(t => !ids.includes(t)) : [];
    const band = ids.every(listable) && front.length && front.every(listable) ? Math.min(BAND, b.height * 0.12) : 0;
    if (band && y < b.top + band) { put(b.left, b.top, b.width, 3, true); return { path, zone: 'stack', before: front[0] }; }
    if (band && y >= b.bottom - band) { put(b.left, b.bottom - 3, b.width, 3, true); return { path, zone: 'stack' }; }
    const fx = (x - b.left) / b.width, fy = (y - b.top) / b.height;
    const near = Math.min(fx, 1 - fx, fy, 1 - fy);
    const zone: Zone = near >= EDGE ? 'center' : near === fx ? 'left' : near === 1 - fx ? 'right' : near === fy ? 'top' : 'bottom';
    const st = bodyEl.querySelector<HTMLElement>(':scope > .dock-stack.dock-on');
    if (zone === 'center' && st && band) {
      // over a stack: between two of its panels, before the first whose middle is below the pointer
      const heads = [...st.querySelectorAll<HTMLElement>(':scope > .dock-tab')], tops = heads.map(h => h.getBoundingClientRect().top);
      const last = st.lastElementChild!.getBoundingClientRect().bottom;
      const k = tops.findIndex((t, i) => y < (t + (tops[i + 1] ?? last)) / 2);
      put(b.left, Math.min(b.bottom - 3, Math.max(b.top, (k < 0 ? last : tops[k]) - 1)), b.width, 3, true);
      return { path, zone: 'stack', before: k < 0 ? undefined : heads[k].dataset.tab };
    }
    if (zone === 'center') put(b.left, b.top, b.width, b.height);
    else if (zone === 'left') put(b.left, b.top, b.width / 2, b.height);
    else if (zone === 'right') put(b.left + b.width / 2, b.top, b.width / 2, b.height);
    else if (zone === 'top') put(b.left, b.top, b.width, b.height / 2);
    else put(b.left, b.top + b.height / 2, b.width, b.height / 2);
    return { path, zone };
  }
}
